// Customer auth context — separate from admin auth. Handles email/password
// login (Bearer token in localStorage) AND Emergent-managed Google OAuth
// (httpOnly cookie). API calls send both credentials so either flow works.
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sc_customer_token";

const CustomerAuthContext = createContext(null);

// Axios instance that always sends credentials + Bearer token if we have one
export const customerApi = axios.create({ baseURL: API, withCredentials: true });
customerApi.interceptors.request.use((cfg) => {
    const tok = localStorage.getItem(TOKEN_KEY);
    if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
    return cfg;
});

export function CustomerAuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await customerApi.get("/customer/auth/me");
            setUser(data);
            return data;
        } catch {
            setUser(null);
            return null;
        }
    }, []);

    useEffect(() => {
        // If returning from OAuth callback, let AuthCallback handle it — skip /me check
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        (async () => {
            await refresh();
            setLoading(false);
        })();
    }, [refresh]);

    const login = async (email, password) => {
        const { data } = await customerApi.post("/customer/auth/login", { email, password });
        localStorage.setItem(TOKEN_KEY, data.access_token);
        setUser(data.user);
        return data.user;
    };

    const signup = async (payload) => {
        const { data } = await customerApi.post("/customer/auth/signup", payload);
        localStorage.setItem(TOKEN_KEY, data.access_token);
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try { await customerApi.post("/customer/auth/logout"); } catch { /* ignore */ }
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
    };

    const value = { user, loading, login, signup, logout, refresh, setUser };
    return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export function useCustomerAuth() {
    const ctx = useContext(CustomerAuthContext);
    if (!ctx) throw new Error("useCustomerAuth must be used inside CustomerAuthProvider");
    return ctx;
}

/**
 * Redirect to Emergent-managed Google Auth.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export function startGoogleSignIn() {
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}
